import { FunctionComponent } from "react";
import { useTranslation } from "react-i18next";
import { faEnvelope } from "@fortawesome/pro-regular-svg-icons";
import { faLinkedinIn } from "@fortawesome/free-brands-svg-icons";

import { Icon } from "@/components";
import { classNames } from "@/utils/classNames";

import { OverviewColumn } from "../config/overviewColumns";
import {
	OverviewChannel,
	OverviewLastActivity,
	OverviewMetrics,
	OverviewSender,
} from "../schema/overview";
import { formatNumber } from "../utils/percentage";

/** Every rate is nullable: no denominator yet reads as "—", never as 0%. */
const formatRate = (value: number | null): string =>
	value === null ? "—" : `${value.toFixed(1)}%`;

export const ChannelsCell: FunctionComponent<{ channels: OverviewChannel[] }> = ({ channels }) => {
	const { t } = useTranslation();

	if (channels.length === 0) {
		return <span className="text-text-disabled">—</span>;
	}

	return (
		<span className="flex flex-row items-center gap-2" title={channels.join(", ")}>
			{channels.includes("linkedin") ? (
				<Icon
					aria-label={t("analytics.overview.channels.linkedin")}
					className="w-3.5 h-3.5 text-brand-light"
					icon={faLinkedinIn}
				/>
			) : null}
			{channels.includes("email") ? (
				<Icon
					aria-label={t("analytics.overview.channels.email")}
					className="w-3.5 h-3.5 text-brand-light"
					icon={faEnvelope}
				/>
			) : null}
		</span>
	);
};

const MAX_VISIBLE_SENDERS = 3;

export const SendersCell: FunctionComponent<{ senders: OverviewSender[] }> = ({ senders }) => {
	if (senders.length === 0) {
		return <span className="text-text-disabled">—</span>;
	}

	const visible = senders.slice(0, MAX_VISIBLE_SENDERS);
	const overflow = senders.length - visible.length;

	return (
		<span className="flex flex-row items-center">
			{visible.map((sender, index) => (
				<span
					key={sender.name}
					className={classNames(
						"flex items-center justify-center w-6 h-6 rounded-full bg-blue-action-200 text-[10px] font-bold text-brand-light border border-brand-white",
						index > 0 ? "-ml-1.5" : ""
					)}
					title={sender.name}
				>
					{sender.initials}
				</span>
			))}
			{overflow > 0 ? (
				<span
					className="flex items-center justify-center w-6 h-6 -ml-1.5 rounded-full bg-background-light text-[10px] font-bold text-text-meta border border-brand-white"
					title={senders
						.slice(MAX_VISIBLE_SENDERS)
						.map((sender) => sender.name)
						.join(", ")}
				>
					+{overflow}
				</span>
			) : null}
		</span>
	);
};

export const LastActivityCell: FunctionComponent<{ activity: OverviewLastActivity | null }> = ({
	activity,
}) => {
	const { t } = useTranslation();

	if (!activity) {
		return <span className="text-text-disabled">—</span>;
	}

	const at = new Date(activity.at);
	const kindLabel = t(`analytics.overview.activityKinds.${activity.kind}`, {
		defaultValue: t("analytics.overview.activityKinds.other"),
	});

	return (
		<span
			className="flex flex-col items-end leading-tight"
			title={`${kindLabel} · ${at.toLocaleString()}`}
		>
			<span className="text-xs font-semibold text-text-meta">
				{at.toLocaleDateString(undefined, { month: "short", day: "numeric" })}
			</span>
			<span className="text-[10px] text-text-disabled">
				{at.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" })}
			</span>
		</span>
	);
};

/** Renders one metric column for a row. Meta columns are drawn by the row itself. */
export const MetricCell: FunctionComponent<{
	column: OverviewColumn;
	metrics: OverviewMetrics;
}> = ({ column, metrics }) => {
	const tone = column.positive ? "text-status-success" : "text-text";

	if (column.kind === "rate") {
		return (
			<span className={classNames("tabular-nums", tone)}>
				{formatRate(metrics[column.field as keyof OverviewMetrics] as number | null)}
			</span>
		);
	}

	if (column.kind === "count") {
		return (
			<span className="tabular-nums text-text">
				{formatNumber(metrics[column.field as keyof OverviewMetrics] as number)}
			</span>
		);
	}

	const value = metrics[column.field as keyof OverviewMetrics] as number;
	const total = metrics[column.totalField as keyof OverviewMetrics] as number;
	const rate = metrics[column.rateField as keyof OverviewMetrics] as number | null;

	return (
		<span className="tabular-nums text-text whitespace-nowrap">
			<b className="font-semibold">{formatNumber(value)}</b>
			<span className="text-text-meta text-xs ml-1">
				/{formatNumber(total)} ({formatRate(rate)})
			</span>
		</span>
	);
};
